import { useEffect, useMemo, useState } from 'react';
import { AlertCircle, Loader2 } from 'lucide-react';
import type { SimulationBatch, SimulationConversation } from '@/types/evaluator';
import { DataSurface } from '@/components/platform/DataSurface';
import { Skeleton } from '@/components/ui/skeleton';
import { ALL_POPULATIONS } from '../constants';
import type { PersonaSortOption } from '../types';
import { buildPersonaReviewItems, sortPersonaReviewItems } from '../utils';
import { PersonaQueue } from '../components/PersonaQueue';
import { ConversationReview } from '../components/ConversationReview';
import { ResultsEmptyState } from '../components/ResultsEmptyState';

export function ResultsView({
  selectedRun,
  conversations,
  selectedPersonaId,
  selectedConversationId,
  selectedPopulation,
  isLoading,
  isError,
  onSelectPersona,
  onSelectConversation,
}: {
  selectedRun: SimulationBatch | null;
  conversations: SimulationConversation[];
  selectedPersonaId: string | null;
  selectedConversationId: string | null;
  selectedPopulation: string;
  isLoading: boolean;
  isError: boolean;
  onSelectPersona: (id: string) => void;
  onSelectConversation: (id: string) => void;
}) {
  const [personaSortBy, setPersonaSortBy] = useState<PersonaSortOption>('needs_review');
  const allPersonas = useMemo(() => buildPersonaReviewItems(conversations), [conversations]);
  const personas = useMemo(() => {
    const filtered = selectedPopulation === ALL_POPULATIONS
      ? allPersonas
      : allPersonas.filter((persona) => persona.population === selectedPopulation);
    return sortPersonaReviewItems(filtered, personaSortBy);
  }, [allPersonas, personaSortBy, selectedPopulation]);
  const selectedPersona = personas.find((persona) => persona.id === selectedPersonaId) ?? personas[0] ?? null;

  useEffect(() => {
    if (personas.length === 0) return;
    if (!selectedPersonaId || !personas.some((persona) => persona.id === selectedPersonaId)) {
      onSelectPersona(personas[0].id);
      return;
    }
    const persona = personas.find((item) => item.id === selectedPersonaId);
    if (persona && (!selectedConversationId || !persona.conversations.some((item) => item.id === selectedConversationId))) {
      onSelectConversation(persona.conversations[0]?.id ?? '');
    }
  }, [onSelectConversation, onSelectPersona, personas, selectedConversationId, selectedPersonaId]);

  return (
    <div>
      {isLoading ? (
        <DataSurface className="h-[calc(100vh-18rem)] min-h-[560px]">
          <div className="grid h-full lg:grid-cols-[360px_minmax(0,1fr)]">
            <div className="space-y-2 border-r p-3">
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} className="h-20 rounded-md" />
              ))}
            </div>
            <div className="flex items-center justify-center">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          </div>
        </DataSurface>
      ) : isError ? (
        <DataSurface>
          <div className="flex flex-col items-center justify-center gap-3 py-16 text-center">
            <AlertCircle className="h-8 w-8 text-destructive" />
            <p className="text-sm font-medium text-foreground">Could not load this simulation.</p>
            <p className="text-sm text-muted-foreground">Select another run from History or try again shortly.</p>
          </div>
        </DataSurface>
      ) : personas.length === 0 ? (
        <ResultsEmptyState selectedRun={selectedRun} />
      ) : (
        <DataSurface className="h-[calc(100vh-18rem)] min-h-[620px]">
          <div className="grid h-full min-w-0 lg:grid-cols-[360px_minmax(0,1fr)]">
            <PersonaQueue
              personas={personas}
              selectedPersonaId={selectedPersona?.id ?? null}
              sortBy={personaSortBy}
              onSortChange={setPersonaSortBy}
              onSelectPersona={(id) => {
                onSelectPersona(id);
                const persona = personas.find((item) => item.id === id);
                onSelectConversation(persona?.conversations[0]?.id ?? '');
              }}
            />
            <ConversationReview
              persona={selectedPersona}
              selectedConversationId={selectedConversationId}
              onSelectConversation={onSelectConversation}
            />
          </div>
        </DataSurface>
      )}
    </div>
  );
}
